import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './features/auth/AuthContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import LearningHardRedirect from './components/common/LearningHardRedirect';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import AdminAuthLayout from './layouts/AdminAuthLayout';

// Route Guards
import ProtectedRoute    from './features/auth/components/ProtectedRoute';
import AdminRoute        from './features/auth/components/AdminRoute';
import InternshipRoute        from './features/auth/components/InternshipRoute';
import { InternshipBrowseRoute } from './features/auth/components/InternshipRoute';

// Eagerly loaded pages (above-the-fold / auth critical)
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminLoginPage from './pages/AdminLoginPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import VerifyCertificatePage from './pages/VerifyCertificatePage';
import FAQPage from './pages/FAQPage';
import TermsPage from './pages/TermsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import RefundPolicyPage from './pages/RefundPolicyPage';
import CancellationPolicyPage from './pages/CancellationPolicyPage';
import ContactUsPage from './pages/ContactUsPage';
import SubmissionGuidePage from './pages/SubmissionGuidePage';
import PartnerAuthPage from './features/auth/components/PartnerAuthPage';

// Styles
import './styles/App.css';

// Lazy loaded pages (loaded on demand)
const InternshipPaymentPage     = lazy(() => import('./pages/InternshipPaymentPage'));
const InternshipDashboardPage   = lazy(() => import('./pages/InternshipDashboardPage'));
const ProjectSelectionPage      = lazy(() => import('./pages/ProjectSelectionPage'));
const ProjectDetailPage         = lazy(() => import('./pages/ProjectDetailPage'));
const StageDetailPage           = lazy(() => import('./pages/StageDetailPage'));
const CertificatePage           = lazy(() => import('./pages/CertificatePage'));
const AdminOrgsPage             = lazy(() => import('./pages/AdminOrgsPage'));
const AdminOrgDetailPage        = lazy(() => import('./pages/AdminOrgDetailPage'));
const AdminUsersPage            = lazy(() => import('./pages/AdminUsersPage'));
const AdminProjectsPage         = lazy(() => import('./pages/AdminProjectsPage'));
const AdminProjectDetailPage    = lazy(() => import('./pages/AdminProjectDetailPage'));
const AdminStageReviewsPage     = lazy(() => import('./pages/AdminStageReviewsPage'));
const AdminStatsPage            = lazy(() => import('./pages/AdminStatsPage'));
const AdminPricingPage          = lazy(() => import('./pages/AdminPricingPage'));

const PageLoader = () => <LoadingSpinner size="md" label="Loading…" fullPage />;

function App() {
  return (
    <Router>
      <AuthProvider>
        <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public ─────────────────────────────────────────── */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/cancellation-policy" element={<CancellationPolicyPage />} />
            <Route path="/contact-us" element={<ContactUsPage />} />
            <Route path="/submission-guide" element={<SubmissionGuidePage />} />

            {/* ── Auth (no layout) ───────────────────────────────── */}
            <Route element={<AuthLayout />}>
              <Route path="/login"    element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password"  element={<ResetPasswordPage />} />
            </Route>

            {/* ── Partner SSO (TwinGP) — outside AuthLayout so logged-in redirect doesn't block */}
            <Route path="/auth/partner/twingp" element={<PartnerAuthPage />} />

            {/* ── Email Verification (public, no layout) ─────────── */}
            <Route path="/verify-email" element={<VerifyEmailPage />} />

            {/* ── Certificate Verification (public, no layout) ────── */}
            <Route path="/verify-certificate/:certId" element={<VerifyCertificatePage />} />

            {/* ── Deprecated learning routes → external learning app ─ */}
            <Route path="/dashboard" element={<ProtectedRoute><LearningHardRedirect /></ProtectedRoute>} />
            <Route path="/problems" element={<ProtectedRoute><LearningHardRedirect /></ProtectedRoute>} />
            <Route path="/problems/:problemId/submissions" element={<ProtectedRoute><LearningHardRedirect /></ProtectedRoute>} />
            <Route path="/exam/:problemId" element={<ProtectedRoute><LearningHardRedirect /></ProtectedRoute>} />
            <Route path="/submission/:submissionId" element={<ProtectedRoute><LearningHardRedirect /></ProtectedRoute>} />
            <Route path="/learning" element={<LearningHardRedirect />} />
            <Route path="/wallet" element={<ProtectedRoute><Navigate to="/internship/projects" replace /></ProtectedRoute>} />

            {/* ── Internship Payment Gate (no InternshipRoute guard) */}
            {/* Accessible to internship users regardless of status    */}
            <Route path="/internship/payment" element={
              <ProtectedRoute><InternshipPaymentPage /></ProtectedRoute>
            } />

            {/* ── Internship Module (requires active internship) ──── */}
            <Route element={<MainLayout />}>
              <Route path="/internship/dashboard" element={
                <InternshipRoute><InternshipDashboardPage /></InternshipRoute>
              } />
              <Route path="/internship/projects" element={
                <InternshipBrowseRoute><ProjectSelectionPage /></InternshipBrowseRoute>
              } />
              <Route path="/internship/projects/:projectId" element={
                <InternshipBrowseRoute><ProjectDetailPage /></InternshipBrowseRoute>
              } />
              <Route path="/internship/stage/:stageId" element={
                <InternshipRoute><StageDetailPage /></InternshipRoute>
              } />
              <Route path="/internship/certificate" element={
                <InternshipRoute><CertificatePage /></InternshipRoute>
              } />
            </Route>

            {/* ── Admin Login (separate portal, not regular AuthLayout) */}
            <Route element={<AdminAuthLayout />}>
              <Route path="/admin/login" element={<AdminLoginPage />} />
            </Route>

            {/* ── Admin Panel ────────────────────────────────────── */}
            <Route path="/admin" element={
              <AdminRoute><AdminDashboardPage /></AdminRoute>
            } />
            <Route path="/admin/orgs" element={
              <AdminRoute><AdminOrgsPage /></AdminRoute>
            } />
            <Route path="/admin/orgs/:id" element={
              <AdminRoute><AdminOrgDetailPage /></AdminRoute>
            } />
            <Route path="/admin/users" element={
              <AdminRoute><AdminUsersPage /></AdminRoute>
            } />
            <Route path="/admin/projects" element={
              <AdminRoute><AdminProjectsPage /></AdminRoute>
            } />
            <Route path="/admin/projects/:id" element={
              <AdminRoute><AdminProjectDetailPage /></AdminRoute>
            } />
            <Route path="/admin/reviews" element={
              <AdminRoute><AdminStageReviewsPage /></AdminRoute>
            } />
            <Route path="/admin/stats" element={
              <AdminRoute><AdminStatsPage /></AdminRoute>
            } />
            <Route path="/admin/pricing" element={
              <AdminRoute><AdminPricingPage /></AdminRoute>
            } />

            {/* ── Fallback ───────────────────────────────────────── */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </ErrorBoundary>
      </AuthProvider>
    </Router>
  );
}

export default App;
